/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';

import { IAccountAndUser } from '@fleetmatics/ui.insights';

import { environment } from '../../../../environments/environment';
import { ArrayUtilities, isElementNull } from '../../../../utils';
import { IUserCredentials } from '../../../core/models';
import { ExternalModuleLoaded, StartVehiclesPolling } from '../../../core/store/actions';
import {
  getIsMapOptionsOpened,
  getIsReplayOpen,
  getPendoConfiguration,
  getSelectedVehicleIds,
  getUserCredentials
} from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';
import { IWebsocketConfig } from '../../models';
import { WebsocketConnectorService } from '../../services';

@Component({
  selector: 'fm-external-container',
  templateUrl: './external-container.component.html',
  styleUrls: ['./external-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  public pendoConfiguration$: Observable<IAccountAndUser>;
  public userCredentials$: Observable<IUserCredentials>;
  public vehiclesIds$: Observable<any>;
  public isReplayOpen$: Observable<boolean>;
  public isMapOptionsOpened$: Observable<boolean>;

  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _store: Store<IAppState>,
    private readonly _websocketConnector: WebsocketConnectorService
  ) {}

  ngOnInit(): void {
    this.pendoConfiguration$ = this._store.pipe(select(getPendoConfiguration));
    this.userCredentials$ = this._store.pipe(select(getUserCredentials));
    this.vehiclesIds$ = this._store.pipe(
      select(getSelectedVehicleIds),
      distinctUntilChanged((first, second) => ArrayUtilities.compareSets(first, second)),
      map(ids => [...ids])
    );
    this.isReplayOpen$ = this._store.pipe(select(getIsReplayOpen));
    this.isMapOptionsOpened$ = this._store.pipe(select(getIsMapOptionsOpened));

    this._initializeVehicleUpdates();
  }

  ngAfterViewInit(): void {
    this._store.dispatch(new ExternalModuleLoaded());
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private _initializeVehicleUpdates(): void {
    if (!environment.config.isPushServiceDisabled) {
      this._initSignalRSocket();
    } else {
      this._startVehicleSubscriptionPolling();
    }
  }

  private _initSignalRSocket() {
    this.userCredentials$.pipe(filter(userCredentials => !isElementNull(userCredentials), take(1))).subscribe(userCredentials => {
      const config: IWebsocketConfig = {
        userId: userCredentials.userId,
        accountId: userCredentials.accountId
      };

      this._websocketConnector.start(config);
    });

    this.vehiclesIds$.pipe(takeUntil(this._unsubscribe$)).subscribe(vehicleIds => this._websocketConnector.subscribeVehicles(vehicleIds));
  }

  private _startVehicleSubscriptionPolling(): void {
    setTimeout(() => {
      this._store.dispatch(new StartVehiclesPolling());
    }, 10000);
  }
}
